import classNames from "classnames";
import React from "react";
import { msgid, ngettext, t } from "ttag";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { useMountEffect } from "../../hooks/useMountEffect";
import {
    CountdownTimerTheme,
    ICountdownTime,
    ICountdownTimer,
} from "../../models/countdown-timer";
import { BreakPoint } from "../../models/screen";
import { getViewportBreakpoint } from "../../utils/detectMobile";

import styles from "./CountdownTimerDisplay.module.scss";

function formatNumber(n: number | null): string {
    if (n === null) {
        return "";
    }
    // Use browser's locale for proper numeral system support (e.g., Arabic-Indic ٠١)
    return n.toLocaleString(undefined, {
        minimumIntegerDigits: 2,
        useGrouping: false,
    });
}

function calcTimeDiff(
    currentTimer: ICountdownTimer | null,
    onTimerExpired: () => void,
): ICountdownTime | null {
    const now = new Date().getTime();
    let end = 0;
    if (currentTimer) {
        end = Date.parse(currentTimer.end_date);
    }
    const difference = end - now;

    if (difference > 0) {
        const timeLeft: ICountdownTime = {
            days: Math.floor(difference / (1000 * 60 * 60 * 24)),
            hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
            minutes: Math.floor((difference / 1000 / 60) % 60),
            seconds: Math.floor((difference / 1000) % 60),
        };
        return timeLeft;
    } else {
        onTimerExpired();
        return null;
    }
}

function getBodyCopy(currentTimer: ICountdownTimer | null): string {
    if (currentTimer) {
        let body = currentTimer.body;
        if (
            getViewportBreakpoint() < BreakPoint.SMALL &&
            currentTimer.mobile_body
        ) {
            body = currentTimer.mobile_body;
        }
        return body;
    }
    return "";
}

function getThemeStyle(timerTheme: CountdownTimerTheme | undefined): string {
    switch (timerTheme) {
        case CountdownTimerTheme.RED:
            return styles.red;
        case CountdownTimerTheme.YELLOW:
            return styles.yellow;
        case CountdownTimerTheme.CARDS:
            return styles.cards;
        default:
            return "";
    }
}

export const CountdownTimer = (props: {
    currentTimer: ICountdownTimer | null;
    onTimerExpired: () => void;
}): React.ReactNode => {
    const [timeLeft, setTimeLeft] = React.useState<ICountdownTime | null>({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
    });

    useMountEffect(() => {
        const window = getWindow();
        if (!window) return;

        const timerID = window.setInterval(() => {
            setTimeLeft(calcTimeDiff(props.currentTimer, props.onTimerExpired));
        }, 1000);

        return () => {
            clearInterval(timerID);
        };
    });

    if (!timeLeft) {
        return <></>;
    }

    // Extract values for ngettext (can't use logical expressions directly)
    const days = timeLeft.days ?? 0;
    const minutes = timeLeft.minutes ?? 0;
    const seconds = timeLeft.seconds ?? 0;

    const formattedDays = formatNumber(timeLeft.days);
    const formattedHours = formatNumber(timeLeft.hours);
    const formattedMin = formatNumber(timeLeft.minutes);
    const formattedSec = formatNumber(timeLeft.seconds);
    const hasTime =
        formattedDays !== "00" ||
        formattedHours !== "00" ||
        formattedMin !== "00" ||
        formattedSec !== "00";

    const themeStyle = getThemeStyle(props.currentTimer?.theme);

    const timerClasses = classNames({
        [styles.countdownTimer]: true,
        [themeStyle]: true,
    });

    // TODO : Use better semnatic markup for date layout below. Table?
    return (
        <>
            {/* Added condition "hasTime" so that this promo copy is rendered at the same time as the countdown timer. */}
            {hasTime && props.currentTimer && (
                <div
                    className={styles.content}
                    dangerouslySetInnerHTML={{
                        __html: getBodyCopy(props.currentTimer),
                    }}
                ></div>
            )}
            {hasTime && (
                <div className={timerClasses}>
                    <div>
                        <h2>
                            <span className="countdown-timer__copy--trim">
                                {t`Sale`} <br />
                            </span>
                            {t`Ends In`}
                        </h2>
                    </div>
                    <div>
                        <div>
                            <div>
                                <span>{formattedDays}</span>
                                <span>
                                    {ngettext(msgid`day`, `days`, days)}
                                </span>
                            </div>
                            <div className={`${styles.colon} colon`}>:</div>
                        </div>
                        <div>
                            <div>
                                <span>{formattedHours}</span>
                                {/* TODO(i18n): Use ngettext for plurals and translate complete word: {ngettext(msgid`${formattedHours} hour`, `${formattedHours} hours`, timeLeft.hours)} - don't split "hours" into fragments NOTE JWB: Not sure how to accompolish this as the truncation of the text is currently tied to a CSS class*/}
                                <span>
                                    h
                                    <span className="countdown-timer__copy--trim">
                                        ou
                                    </span>
                                    rs
                                </span>
                            </div>
                            <div className={`${styles.colon} colon`}>:</div>
                        </div>
                        <div>
                            <div>
                                <span>{formattedMin}</span>
                                <span>
                                    {ngettext(msgid`min`, `mins`, minutes)}
                                </span>
                            </div>
                            <div className={`${styles.colon} colon`}>:</div>
                        </div>
                        <div>
                            <div>
                                <span>{formattedSec}</span>
                                <span>
                                    {ngettext(msgid`sec`, `secs`, seconds)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
